import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCode, faTimes, faPlus, faX } from '@fortawesome/free-solid-svg-icons';
import { faCircleCheck } from '@fortawesome/free-regular-svg-icons';

interface Experience {
  company: string;
  totalDurationMonths: number;
  isCurrent: boolean;
  endYear?: number;
  roles?: Role[];
}

interface Role {
  title: string;
  duration: number;
  isCurrent: boolean;
  endYear?: number;
  startDate?: string | { year: number; month: number };
  endDate?: string | { year: number; month: number };
  isFreelance: boolean;
  employmentStatus: string;
  location: string;
  skills: string[];
  description?: string;
}

interface Profile {
  id: string;
  candidateName?: string;
  email?: string;
  phone?: string;
  linkedInLink?: string;
  profileUrl?: string;
  summary?: string;
  industries?: string[];
  experiences?: Experience[];
  anonymizedProfile?: any;
}

interface ProfileDetailsProps {
  profile: Profile;
  onAddToShortlist?: (profileId: string) => void;
  isInShortlist?: boolean;
  showAddToShortlist?: boolean;
  showCloseButton?: boolean;
  onClose?: () => void;
}

const ProfileDetails: React.FC<ProfileDetailsProps> = ({
  profile,
  onAddToShortlist,
  isInShortlist = false,
  showAddToShortlist = true,
  showCloseButton = false,
  onClose
}) => {
  // Get the most recent role for display
  const currentRole = profile.experiences?.[0]?.roles?.[0];

  // Calculate total experience
  const totalExperienceMonths = profile.experiences?.reduce((total, exp) =>
    total + (exp.totalDurationMonths || 0), 0) || 0;
  const totalExperienceYears = Math.round(totalExperienceMonths / 12);

  const formatDate = (date?: string | { year: number; month: number }) => {
    if (!date) return '';

    // If it's an object with year and month, format it
    if (typeof date === 'object' && date.year && date.month) {
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${monthNames[date.month - 1]} ${date.year}`;
    }

    // If it's already a formatted string, return it
    if (typeof date === 'string') {
      return date;
    }

    return '';
  };

  const formatDuration = (months: number) => {
    const years = Math.floor(months / 12);
    const remainingMonths = months % 12;

    if (years === 0) {
      return `${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    } else if (remainingMonths === 0) {
      return `${years} year${years !== 1 ? 's' : ''}`;
    } else {
      return `${years} year${years !== 1 ? 's' : ''} ${remainingMonths} month${remainingMonths !== 1 ? 's' : ''}`;
    }
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="p-4 sm:p-6" style={{ borderBottom: '1px solid #E5E7EB' }}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-3 flex-1 min-w-0">
            <div className="w-8 h-8 rounded flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#F3F4F6' }}>
              <FontAwesomeIcon
                icon={faCode}
                className="w-5 h-5"
                style={{ color: '#03238C' }}
              />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-col sm:flex-row sm:items-center space-y-1 sm:space-y-0 sm:space-x-3 mb-1">
                <h2
                  className="font-bold truncate"
                  style={{
                    fontSize: '18px',
                    color: '#03238C',
                    fontWeight: '700',
                    fontFamily: 'Inter, sans-serif',
                    letterSpacing: '-0.04em'
                  }}
                >
                  {currentRole?.title || 'Software Engineer'}
                </h2>
                <div className="flex items-center space-x-1">
                  <FontAwesomeIcon
                    icon={faCircleCheck}
                    className="w-4 h-4"
                    style={{ color: '#0034E6' }}
                  />
                  <span
                    style={{
                      color: '#0034E6',
                      fontSize: '14px',
                      fontWeight: '600',
                      fontFamily: 'Inter, sans-serif'
                    }}
                  >
                    Verified by DreamDev
                  </span>
                </div>
              </div>
              {/* <p className="text-sm" style={{ color: '#6B7280' }}>{totalExperienceYears} years of experience</p> */}
            </div>
          </div>
          {showCloseButton && onClose && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <FontAwesomeIcon icon={faTimes} className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Add to Shortlist Button in Header */}
        {showAddToShortlist && onAddToShortlist && (
          <div className="flex justify-center sm:justify-start">
            <button
              onClick={() => onAddToShortlist(profile.id)}
              className="px-4 py-2 text-sm font-bold uppercase flex items-center justify-center space-x-2 transition-colors duration-200 w-full sm:w-auto"
              style={{
                backgroundColor: 'rgba(255, 92, 62, 0.1)',
                color: '#FF5C3E',
                fontSize: 'var(--dreamdev-font-size-sm)',
                fontWeight: '700',
                borderRadius: '6px',
                border: '1px solid #FF5C3E',
                textShadow: 'none'
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.2)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.backgroundColor = 'rgba(255, 92, 62, 0.1)';
              }}
            >
              <FontAwesomeIcon
                icon={isInShortlist ? faX : faPlus}
                className="w-3 h-3"
              />
              <span>{isInShortlist ? 'REMOVE FROM SHORTLIST' : 'ADD TO SHORTLIST'}</span>
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-6">

        {/* Industries */}
        {profile.industries && profile.industries.length > 0 && (
          <div>
            <h3
              className="font-semibold mb-3"
              style={{
                color: '#03238C',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              Industries
            </h3>
            <div className="flex flex-wrap gap-2">
              {profile.industries.map((industry, index) => (
                <span
                  key={index}
                  className="px-3 py-1 rounded-full text-sm"
                  style={{
                    backgroundColor: '#CEECFF',
                    color: '#03238C',
                    fontSize: '14px',
                    fontWeight: '400',
                    fontFamily: 'Source Sans 3, sans-serif'
                  }}
                >
                  {industry}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* About Section */}
        {profile.summary && (
          <div>
            <h3
              className="font-semibold mb-3"
              style={{
                color: '#03238C',
                fontSize: '16px',
                fontWeight: '600',
                fontFamily: 'Inter, sans-serif'
              }}
            >
              About
            </h3>
            <p
              className="text-sm leading-relaxed"
              style={{
                color: '#374151',
                fontSize: '14px',
                fontFamily: 'Source Sans 3, sans-serif',
                lineHeight: '1.6'
              }}
            >
              {profile.summary}
            </p>
          </div>
        )}

        {/* Professional Experience */}
        <div>
          <h3
            className="font-semibold mb-4"
            style={{
              color: '#03238C',
              fontSize: '16px',
              fontWeight: '600',
              fontFamily: 'Inter, sans-serif'
            }}
          >
            Professional Experience
          </h3>
          <div className="space-y-6">
            {profile.experiences?.map((experience, expIndex) =>
              experience.roles?.map((role, roleIndex) => (
                <div key={`${expIndex}-${roleIndex}`} className="pl-4" style={{ borderLeft: '2px solid #E5E7EB' }}>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center" style={{ backgroundColor: '#F3F4F6' }}>
                        <FontAwesomeIcon
                          icon={faCode}
                          className="w-4 h-4"
                          style={{ color: '#03238C' }}
                        />
                      </div>
                      <div>
                        <h4
                          className="font-semibold"
                          style={{
                            color: '#03238C',
                            fontSize: '16px',
                            fontWeight: '600',
                            fontFamily: 'Inter, sans-serif'
                          }}
                        >
                          {role.title}
                        </h4>
                        <p
                          className="text-sm"
                          style={{
                            color: '#6B7280',
                            fontSize: '14px',
                            fontFamily: 'Source Sans 3, sans-serif'
                          }}
                        >
                          {experience.company} • {formatDuration(role.duration)} • {role.location}
                        </p>
                        <p
                          className="text-xs"
                          style={{
                            color: '#9CA3AF',
                            fontSize: '12px',
                            fontFamily: 'Source Sans 3, sans-serif'
                          }}
                        >
                          {formatDate(role.startDate)} - {role.isCurrent ? 'Present' : formatDate(role.endDate)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {role.description && (
                    <p
                      className="text-sm leading-relaxed mb-3"
                      style={{
                        color: '#374151',
                        fontSize: '14px',
                        fontFamily: 'Source Sans 3, sans-serif',
                        lineHeight: '1.6'
                      }}
                    >
                      {role.description}
                    </p>
                  )}

                  {role.skills && role.skills.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {role.skills.map((skill, skillIndex) => (
                        <span
                          key={skillIndex}
                          className="px-2 py-1 rounded text-xs"
                          style={{
                            backgroundColor: '#F3F4F6',
                            color: '#374151',
                            fontSize: '12px',
                            fontFamily: 'Source Sans 3, sans-serif'
                          }}
                        >
                          {skill}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

      </div>
    </div>
  );
};

export default ProfileDetails;
